import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { TaskForm } from "@/components/TaskForm";
import { useCreateTask } from "@/lib/useTaskMutations";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import type { TaskStatus } from "@shared/types";

const STATUS_VALUES = ["backlog", "todo", "doing", "done"] as const;

type NewTaskSearch = { status?: TaskStatus };

function isTaskStatus(v: unknown): v is TaskStatus {
  return (
    typeof v === "string" && (STATUS_VALUES as readonly string[]).includes(v)
  );
}

// Default swatch for newly-created tags. The tag-create endpoint requires a
// color; the user only typed a name, so we pick a sensible neutral that
// matches the design's standard tag swatch. Server stores it; the user
// can recolor from the task detail later.
const DEFAULT_TAG_COLOR = "#94a0b4";

function NewTaskPage() {
  const { projectId } = Route.useParams();
  const { status: initialStatus } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // useCreateTask routes through invalidateForTask, so the full surface set
  // (board, inbox, my-tasks, dashboard, activity) refreshes — not just the
  // current project's task list.
  const create = useCreateTask(projectId, {
    onSuccess: (data) => {
      void navigate({
        to: "/tasks/$taskId",
        params: { taskId: data.task.id },
      });
    },
  });

  return (
    <div className="proj-shell-content" style={{ maxWidth: "none" }}>
      <TaskForm
        mode="page"
        projectId={projectId}
        initialStatus={initialStatus}
        onSubmit={(payload) => {
          create.mutate(
            {
              title: payload.title,
              priority: payload.priority,
              status: payload.status,
              description: payload.description,
              assigneeId: payload.assigneeId ?? null,
              // Server's CreateTaskInput accepts string|undefined for dueDate
              // and string|null|undefined for parentTaskId — omitting the
              // keys when blank avoids a 422 on the optional fields.
              ...(payload.dueDate ? { dueDate: payload.dueDate } : {}),
              ...(payload.parentTaskId
                ? { parentTaskId: payload.parentTaskId }
                : {}),
            },
            {
              // Server's create endpoint takes a single assigneeId, no tag
              // ids, no nested subtasks, no files; we apply all of those
              // post-create. Errors here are non-fatal — the task itself was
              // created successfully, so we navigate anyway.
              onSuccess: async ({ task }) => {
                // Subtask rows from the "break it down" section — each is a
                // child task. (These were silently dropped before.)
                const subtaskTitles = payload.subtaskTitles ?? [];
                for (const title of subtaskTitles) {
                  await api
                    .createTask(projectId, { title, parentTaskId: task.id })
                    .catch(() => undefined);
                }

                // Dropzone files — uploads need the task id, so they run now.
                const files = payload.files ?? [];
                for (const f of files) {
                  await api
                    .uploadAttachment({ taskId: task.id }, f)
                    .catch(() => undefined);
                }

                if (subtaskTitles.length > 0 || files.length > 0) {
                  // The user already navigated to the task detail page —
                  // refresh its panels as the late additions land.
                  void qc.invalidateQueries({
                    queryKey: queryKeys.task(task.id),
                  });
                  void qc.invalidateQueries({
                    queryKey: queryKeys.subtasksOf(task.id),
                  });
                  void qc.invalidateQueries({
                    queryKey: queryKeys.attachmentsForTask(task.id),
                  });
                  void qc.invalidateQueries({
                    queryKey: queryKeys.tasksFor(projectId),
                  });
                }

                const tagNames = payload.tagNames ?? [];
                if (tagNames.length === 0) return;

                // Resolve each typed chip to a tag id. If a tag with the same
                // name (case-insensitive) already exists, reuse it; otherwise
                // create it with the default swatch and use the new id. The
                // tags list is refreshed at the end so the user sees their new
                // tags next time they open the form.
                const existing = await api.listTags().catch(() => ({
                  tags: [] as Awaited<ReturnType<typeof api.listTags>>["tags"],
                }));
                const byName = new Map(
                  existing.tags.map((t) => [t.name.toLowerCase(), t]),
                );
                let createdAny = false;
                for (const name of tagNames) {
                  const hit = byName.get(name.toLowerCase());
                  let tagId = hit?.id;
                  if (!tagId) {
                    try {
                      const { tag } = await api.createTag({
                        name,
                        color: DEFAULT_TAG_COLOR,
                      });
                      tagId = tag.id;
                      createdAny = true;
                    } catch {
                      continue;
                    }
                  }
                  await api.assignTag(task.id, tagId).catch(() => undefined);
                }
                if (createdAny) {
                  void qc.invalidateQueries({ queryKey: queryKeys.tags() });
                }
              },
            },
          );
        }}
        onCancel={() =>
          void navigate({
            to: "/projects/$projectId",
            params: { projectId },
          })
        }
      />
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId/tasks/new")({
  component: NewTaskPage,
  validateSearch: (search: Record<string, unknown>): NewTaskSearch => {
    return isTaskStatus(search.status) ? { status: search.status } : {};
  },
});
